/*
  assets/our-authors.js — "Our Authors" directory.
  Loads every "authors" metaobject from templates/page.authors-data.liquid (250 per request),
  then searches, filters A–Z and paginates entirely in the browser.
*/
(function () {
  'use strict';

  var CONCURRENCY = 4;   // data pages fetched in parallel (keep low to avoid HTTP 429)
  var RETRIES = 3;       // attempts per data page before giving up on it
  var TIMEOUT = 20000;   // ms per request

  /* ---------- helpers ---------- */
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var plain = function (s) {
    return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };
  var sleep = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
  var collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
  var ICON_GO = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

  /* ---------- data ---------- */
  async function fetchPage(baseUrl, n) {
    var url = baseUrl + (baseUrl.indexOf('?') > -1 ? '&' : '?') + 'page=' + n;
    var lastErr;
    for (var attempt = 1; attempt <= RETRIES; attempt++) {
      var ctrl = new AbortController();
      var t = setTimeout(function () { ctrl.abort(); }, TIMEOUT);
      try {
        var res = await fetch(url, { credentials: 'same-origin', signal: ctrl.signal });
        if (res.status === 429 || res.status >= 500) throw new Error('HTTP ' + res.status);
        if (!res.ok) throw Object.assign(new Error('HTTP ' + res.status), { fatal: true });
        var text = await res.text();
        try {
          var json = JSON.parse(text);
          if (!json || !Array.isArray(json.authors)) throw new Error('missing "authors" array');
          return json;
        } catch (e) {
          // Most common cause: the alternate template is missing or a Liquid error was printed.
          throw Object.assign(new Error('Page ' + n + ' is not valid JSON (' + e.message + '). Starts with: ' +
            text.slice(0, 160).replace(/\s+/g, ' ')), { fatal: true });
        }
      } catch (e) {
        lastErr = e;
        if (e.fatal) throw e;
        await sleep(600 * attempt * attempt);   // 0.6s, 2.4s, 5.4s
      } finally {
        clearTimeout(t);
      }
    }
    throw lastErr;
  }

  async function loadAll(cfg, onProgress) {
    var first = await fetchPage(cfg.dataUrl, 1);
    var pages = Math.max(1, Number(first.pages) || 1);
    var results = new Array(pages);
    results[0] = first.authors;
    var queue = [];
    for (var p = 2; p <= pages; p++) queue.push(p);
    var failed = [];
    var done = 1;
    onProgress(done, pages);

    async function worker() {
      while (queue.length) {
        var n = queue.shift();
        try {
          results[n - 1] = (await fetchPage(cfg.dataUrl, n)).authors;
        } catch (e) {
          if (e.fatal) throw e;
          console.warn('[Our Authors] page ' + n + ' failed:', e);
          failed.push(n);
          results[n - 1] = [];
        }
        onProgress(++done, pages);
      }
    }
    var workers = [];
    for (var i = 0; i < Math.min(CONCURRENCY, queue.length); i++) workers.push(worker());
    await Promise.all(workers);

    return { raw: [].concat.apply([], results), total: Number(first.total) || 0, failed: failed };
  }

  function prepare(raw) {
    var seen = new Set();
    var out = [];
    raw.forEach(function (a) {
      if (!a) return;
      var name = String(a.n || '').trim() || String(a.h || '').replace(/-/g, ' ').trim();
      if (!name) return;
      var id = a.h || a.u || name;
      if (seen.has(id)) return;                    // pages can overlap if entries change mid-load
      seen.add(id);

      var cats = (Array.isArray(a.cat) ? a.cat : []).map(function (c) { return String(c || '').trim(); }).filter(Boolean);
      var books = (Array.isArray(a.p) ? a.p : []).filter(function (b) { return b && b.t; });
      var isbns = Array.isArray(a.isbn) ? a.isbn : [];
      var key = plain(name);
      var f = key.charAt(0).toUpperCase();

      out.push({
        name: name, code: a.c || '', url: a.u || '', img: a.i || '', bio: a.b || '',
        cats: cats, books: books, bookCount: Math.max(Number(a.pc) || 0, books.length),
        key: key,
        first: /[A-Z]/.test(f) ? f : '#',
        hay: [key, plain(a.c), cats.map(plain).join(' '), books.map(function (b) { return plain(b.t); }).join(' ')].join(' | '),
        isbnDigits: isbns.map(function (s) { return String(s).replace(/[^0-9xX]/g, ''); }).join(' ').toLowerCase()
      });
    });
    return out.sort(function (x, y) { return collator.compare(x.key, y.key); });
  }

  /* ---------- cache (sessionStorage, per data URL) ---------- */
  function cacheGet(key, minutes) {
    if (!minutes) return null;
    try {
      var v = JSON.parse(sessionStorage.getItem(key));
      if (v && Date.now() - v.t < minutes * 60000 && Array.isArray(v.a)) return v.a;
    } catch (e) {}
    return null;
  }
  function cacheSet(key, authors) {
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), a: authors })); } catch (e) { /* quota full: ignore */ }
  }

  /* ---------- one section ---------- */
  function initSection(root) {
    var cfgEl = root.querySelector('[data-oa-config]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.error('[Our Authors] bad config', e); return; }

    var grid = root.querySelector('[data-oa-grid]');
    var input = root.querySelector('[data-oa-search]');
    var countEl = root.querySelector('[data-oa-count]');
    var emptyEl = root.querySelector('[data-oa-empty]');
    var noticeEl = root.querySelector('[data-oa-notice]');
    var pager = root.querySelector('[data-oa-pager]');
    var letters = Array.prototype.slice.call(root.querySelectorAll('[data-oa-az] [data-letter]'));
    var phTpl = root.querySelector('[data-oa-ph]');
    var placeholder = `<svg {{ shared_svg_attrs }} viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.3336 14.4447C14.7538 14.4447 17.5264 11.6417 17.5264 8.18392C17.5264 4.72616 14.7538 1.9231 11.3336 1.9231C7.91347 1.9231 5.14087 4.72616 5.14087 8.18392C5.14087 11.6417 7.91347 14.4447 11.3336 14.4447Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M20.9678 24.0769C19.5098 20.0278 15.7026 17.3329 11.4404 17.3329C7.17822 17.3329 3.37107 20.0278 1.91309 24.0769" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
    var perPage = parseInt(root.dataset.perPage, 10) || 24;
    var nameTag = cfg.nameTag === 'h2' ? 'h2' : 'h3';
    var cacheKey = 'oa:' + cfg.dataUrl;
    var cacheMinutes = cfg.design ? 0 : Number(cfg.cacheMinutes) || 0;
    var emptyText = emptyEl.textContent;

    var authors = [], filtered = [], letter = 'all', page = 1, timer, wired = false;

    function authorLink(a) {
      return a.url || (cfg.searchUrl + '?type=product&q=' + encodeURIComponent(a.name));
    }

    function card(a) {
      var link = esc(authorLink(a));
      var media = a.img ? '<img src="' + esc(a.img) + '" alt="" width="52" height="52" loading="lazy">' : placeholder;
      var meta = '';

      if (a.cats.length) {
        meta += '<p class="oa-card__row"><span class="oa-card__label">' + esc(cfg.writesLabel) + ':</span> <span class="oa-card__tags">' +
          a.cats.map(function (c) {
            return '<button type="button" class="oa-card__tag" data-oa-genre="' + esc(c) + '">' + esc(c) + '</button>';
          }).join(', ') + '</span></p>';
      }
      if (a.books.length) {
        var shown = a.books.slice(0, 2);
        var more = a.bookCount - shown.length;
        meta += '<p class="oa-card__row"><span class="oa-card__label">' + esc(cfg.booksLabel) + '</span> <span class="oa-card__tags">' +
          shown.map(function (b) { return '<a class="oa-card__tag" href="' + esc(b.u) + '">' + esc(b.t) + '</a>'; }).join(', ') +
          (more > 0 ? ' <a class="oa-card__more" href="' + link + '">+' + more + ' more</a>' : '') + '</span></p>';
      }

      return '<article class="oa-card">' +
        '<a class="oa-card__media" href="' + link + '" tabindex="-1" aria-hidden="true">' + media + '</a>' +
        '<div class="oa-card__body"><' + nameTag + ' class="oa-card__name"><a href="' + link + '">' + esc(a.name) + '</a></' + nameTag + '>' +
        (a.bio ? '<p class="oa-card__bio">' + a.bio + '</p>' : '') + '</div>' +
        (meta ? '<div class="oa-card__meta">' + meta + '</div>' : '') +
        '<div class="oa-card__foot"><a class="oa-card__btn" href="' + link + '">' + esc(cfg.buttonLabel) + '</a>' +
        '<a class="oa-card__go" href="' + link + '" aria-label="' + esc(cfg.buttonLabel) + ': ' + esc(a.name) + '">' + ICON_GO + '</a></div>' +
        '</article>';
    }

    function pageNumbers(cur, total) {
      if (total <= 7) return Array.from({ length: total }, function (_, i) { return i + 1; });
      var set = new Set([1, total, cur - 1, cur, cur + 1]);
      if (cur <= 3) [2, 3, 4].forEach(function (n) { set.add(n); });
      if (cur >= total - 2) [total - 3, total - 2, total - 1].forEach(function (n) { set.add(n); });
      var nums = Array.from(set).filter(function (n) { return n >= 1 && n <= total; }).sort(function (a, b) { return a - b; });
      var out = [];
      nums.forEach(function (n, i) { if (i && n - nums[i - 1] > 1) out.push('…'); out.push(n); });
      return out;
    }

    function renderPager(total) {
      if (total <= 1) { pager.innerHTML = ''; return; }
      var html = '<button type="button" class="oa__page oa__page--prev" data-page="' + (page - 1) + '"' + (page === 1 ? ' disabled' : '') + '>‹ Previous</button>';
      pageNumbers(page, total).forEach(function (n) {
        html += n === '…'
          ? '<span class="oa__gap" aria-hidden="true">…</span>'
          : '<button type="button" class="oa__page' + (n === page ? ' is-current' : '') + '" data-page="' + n + '" aria-label="Page ' + n + '"' + (n === page ? ' aria-current="page"' : '') + '>' + n + '</button>';
      });
      html += '<button type="button" class="oa__page oa__page--next" data-page="' + (page + 1) + '"' + (page === total ? ' disabled' : '') + '>Next ›</button>';
      pager.innerHTML = html;
    }

    function render() {
      var total = Math.max(1, Math.ceil(filtered.length / perPage));
      page = Math.min(Math.max(1, page), total);
      var start = (page - 1) * perPage;
      var slice = filtered.slice(start, start + perPage);

      grid.innerHTML = slice.map(card).join('');
      grid.removeAttribute('aria-busy');
      emptyEl.hidden = filtered.length > 0;
      countEl.textContent = filtered.length
        ? 'Showing ' + (start + 1) + '–' + (start + slice.length) + ' of ' + filtered.length.toLocaleString() + ' authors'
        : 'Showing 0 authors';
      renderPager(filtered.length ? total : 0);
      syncUrl();
    }

    function applyFilters() {
      var q = plain(input.value.trim());
      var qDigits = q.replace(/[^0-9x]/g, '');
      filtered = authors.filter(function (a) {
        return (letter === 'all' || a.first === letter) &&
          (!q || a.hay.indexOf(q) > -1 || (qDigits.length >= 4 && a.isbnDigits.indexOf(qDigits) > -1));
      });
    }

    function update(resetPage) {
      if (resetPage) page = 1;
      applyFilters();
      render();
    }

    function setLetter(l) {
      letter = l;
      letters.forEach(function (b) {
        var on = b.dataset.letter === l;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function syncUrl() {
      if (cfg.design) return;
      var url = new URL(window.location.href);
      var q = input.value.trim();
      letter === 'all' ? url.searchParams.delete('letter') : url.searchParams.set('letter', letter);
      q ? url.searchParams.set('q', q) : url.searchParams.delete('q');
      page > 1 ? url.searchParams.set('pg', page) : url.searchParams.delete('pg');
      history.replaceState(history.state, '', url);
    }

    function scrollToTop() {
      var y = root.getBoundingClientRect().top + window.scrollY - 80;
      if (window.scrollY > y) window.scrollTo({ top: y, behavior: 'smooth' });
    }

    function showError(e) {
      grid.innerHTML = '';
      grid.removeAttribute('aria-busy');
      countEl.textContent = '';
      pager.innerHTML = '';
      emptyEl.hidden = false;
      emptyEl.innerHTML = esc(cfg.error) + ' <button type="button" class="oa__page" data-oa-retry>Try again</button>' +
        (cfg.design && e ? '<br><small>' + esc(e.message) + '</small>' : '');
      emptyEl.querySelector('[data-oa-retry]').addEventListener('click', function () {
        emptyEl.hidden = true;
        emptyEl.textContent = emptyText;
        load(true);
      });
    }

    function showNotice(msg) {
      noticeEl.hidden = !msg;
      noticeEl.textContent = msg || '';
    }

    function wireControls() {
      if (wired) return;
      wired = true;

      letters.forEach(function (b) {
        b.addEventListener('click', function () { setLetter(b.dataset.letter); update(true); });
      });

      input.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(function () { update(true); }, 150);
      });

      pager.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-page]');
        if (!btn || btn.disabled) return;
        page = Number(btn.dataset.page);
        render();
        scrollToTop();
      });

      grid.addEventListener('click', function (e) {
        var g = e.target.closest('[data-oa-genre]');
        if (!g) return;
        input.value = g.dataset.oaGenre;
        setLetter('all');
        update(true);
        scrollToTop();
      });
    }

    function ready(list) {
      authors = list;

      var counts = {};
      authors.forEach(function (a) { counts[a.first] = (counts[a.first] || 0) + 1; });
      letters.forEach(function (b) {
        var l = b.dataset.letter;
        b.disabled = l !== 'all' && !counts[l];
        if (l !== 'all') b.title = (counts[l] || 0) + ' authors';
      });

      wireControls();
      input.disabled = false;

      var params = new URLSearchParams(window.location.search);
      if (params.get('q') && !input.value) input.value = params.get('q');
      var l = (params.get('letter') || letter || 'all');
      l = l === 'all' ? 'all' : l.toUpperCase();
      setLetter(l === 'ALL' || (l !== 'all' && !counts[l]) ? 'all' : l);
      page = parseInt(params.get('pg'), 10) || page || 1;
      update(false);
    }

    async function load(force) {
      var cached = force ? null : cacheGet(cacheKey, cacheMinutes);
      if (cached) { ready(cached); return; }

      grid.setAttribute('aria-busy', 'true');
      countEl.textContent = 'Loading authors…';
      try {
        var result = await loadAll(cfg, function (done, pages) {
          countEl.textContent = 'Loading authors… ' + Math.round(done / pages * 100) + '%';
        });
        var list = prepare(result.raw);
        console.info('[Our Authors] loaded ' + list.length + ' of ' + result.total + ' metaobjects');

        if (result.failed.length) {
          showNotice('Some authors couldn\u2019t be loaded. Reload the page to see the full list.');
        } else {
          showNotice('');
          cacheSet(cacheKey, list);   // only cache complete lists
        }
        ready(list);
      } catch (e) {
        console.error('[Our Authors]', e);
        showError(e);
      }
    }

    load(false);
  }

  function boot() {
    document.querySelectorAll('[data-oa]').forEach(function (root) {
      if (root.dataset.oaReady) return;
      root.dataset.oaReady = '1';
      initSection(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);   // theme editor
})();